// Choice and navigation controls of the whole system (DESIGN.md «Контролы»): checkbox, radio group, switch, chevron, tabs,
// search field and animated count. Screens use these instead of native boxes or their own classes.

import { forwardRef, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ChangeEvent, type InputHTMLAttributes, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { Icon } from './icons.tsx';
import { cx } from './base.tsx';
import s from './controls.module.css';

const setRef = <T,>(ref: Ref<T> | undefined, v: T | null) => { if (typeof ref === 'function') ref(v); else if (ref) (ref as { current: T | null }).current = v; };

/** The tick and the dash drawn inside a box. `pathLength` = 1, so CSS draws the stroke from 0 to 1 whatever the path length. */
function Marks() {
  return (
    <svg className={s.marks} viewBox="0 0 16 16" aria-hidden="true">
      <path className={s.tick} pathLength={1} d="M4 8.5 6.8 11.2 12 5.4" />
      <path className={s.dash} pathLength={1} d="M4.5 8h7" />
    </svg>
  );
}

type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'checked' | 'onChange' | 'type' | 'size'> & {
  /** 'mixed' — part of a group is chosen (the head of a table, «Выбрать все»). */
  checked: boolean | 'mixed';
  onChange?: (checked: boolean, e: ChangeEvent<HTMLInputElement>) => void;
  /** The name for a screen reader when there is no visible text. */
  label?: string;
  invalid?: boolean;
  children?: ReactNode;
};

/**
 * The one checkbox. A real `input` keeps the keyboard (Space), forms and focus of the browser; its look is drawn by the system:
 * the box fills, the tick draws itself by its stroke, the press shrinks the box a little.
 * `className` goes to the outer element: the box when there is no text, the whole line (the label) when there is.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { checked, onChange, label, invalid, children, className, disabled, ...rest }, ref,
) {
  const inner = useRef<HTMLInputElement | null>(null);
  useLayoutEffect(() => { if (inner.current) inner.current.indeterminate = checked === 'mixed'; }, [checked]);
  const box = (
    <span className={cx(s.box, !children && className)} data-invalid={invalid || undefined}>
      <input {...rest} ref={(el) => { inner.current = el; setRef(ref, el); }} type="checkbox" className={s.boxInput}
        checked={checked === true} disabled={disabled} aria-label={children ? rest['aria-label'] : rest['aria-label'] ?? label}
        aria-checked={checked === 'mixed' ? 'mixed' : undefined}
        aria-invalid={invalid || undefined} onChange={(e) => onChange?.(e.target.checked, e)} />
      <Marks />
    </span>
  );
  if (!children) return box;
  return <label className={cx(s.choice, disabled && s.choiceOff, className)}>{box}<span className={s.choiceText}>{children}</span></label>;
});

/** A box that only shows a state (a yes/no cell, a row of a pick list): the same look, no input of its own. */
export function CheckMark({ checked, className }: { checked: boolean | 'mixed'; className?: string }) {
  return (
    <span className={cx(s.box, s.mark, className)} data-on={checked === true || undefined} data-mixed={checked === 'mixed' || undefined} aria-hidden="true">
      <Marks />
    </span>
  );
}

export interface RadioOption<V extends string> { value: V; label: ReactNode; hint?: ReactNode; disabled?: boolean }

/** One answer from a few, all visible at once. Native radios of one name: arrows move the choice, Tab leaves the group. */
export function RadioGroup<V extends string>({ value, onChange, options, label, row, disabled, className }: {
  value: V | null; onChange: (v: V) => void; options: RadioOption<V>[]; label: string; row?: boolean; disabled?: boolean; className?: string;
}) {
  const name = useId();
  return (
    <div role="radiogroup" aria-label={label} className={cx(s.radios, row && s.radiosRow, className)}>
      {options.map((o) => (
        <label key={o.value} className={cx(s.choice, (disabled || o.disabled) && s.choiceOff)}>
          <span className={s.radio}>
            <input type="radio" name={name} value={o.value} className={s.radioInput} checked={value === o.value}
              disabled={disabled || o.disabled} onChange={() => onChange(o.value)} />
          </span>
          <span className={s.choiceText}>{o.label}{o.hint && <span className={s.choiceHint}>{o.hint}</span>}</span>
        </label>
      ))}
    </div>
  );
}

/** A yes/no setting that applies at once. The knob slides; pressed, it stretches toward where it goes. */
export function Switch({ checked, onChange, disabled, label, size = 'md', children }: {
  checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string; size?: 'sm' | 'md'; children?: ReactNode;
}) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
      className={cx(s.switch, size === 'sm' && s.switchSm)} onClick={() => onChange(!checked)}>
      <i aria-hidden="true" />{children}
    </button>
  );
}

type Dir = 'down' | 'right' | 'left' | 'up';
/**
 * The one arrow. `dir` — where it points; with `open` it turns: a right arrow points down when open (a folder, a group),
 * a down arrow points up (a menu trigger). Inside a trigger of a menu it turns by itself while the menu is open.
 */
export function Chevron({ dir = 'down', open, size = 14, className }: { dir?: Dir; open?: boolean; size?: number; className?: string }) {
  return <span className={cx(s.chevron, className)} data-dir={dir} data-open={open || undefined} aria-hidden="true"><Icon name="chevronDown" size={size} /></span>;
}

/**
 * Where the marker of the chosen item stands (the line under a tab, the plate under a segment): measured from the item, so it
 * slides to the next one. The first placement does not slide in from the edge — the transition is on only after it.
 */
export function useMarker(box: React.RefObject<HTMLElement | null>, selector: string, deps: unknown[]) {
  const [mark, setMark] = useState<{ x: number; w: number } | null>(null);
  const [moving, setMoving] = useState(false);
  const measure = useCallback(() => {
    const el = box.current?.querySelector<HTMLElement>(selector);
    setMark(el ? { x: el.offsetLeft, w: el.offsetWidth } : null);
  }, [box, selector]);
  useLayoutEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (box.current) ro.observe(box.current);
    return () => ro.disconnect();
  }, [measure, ...deps]);
  useLayoutEffect(() => {
    if (!mark || moving) return;
    const id = requestAnimationFrame(() => setMoving(true));
    return () => cancelAnimationFrame(id);
  }, [mark, moving]);
  return { style: mark ? { transform: `translateX(${mark.x}px)`, width: mark.w } : undefined, moving, shown: !!mark };
}

/** Arrows, Home and End over a row of items: the next key, or null when the key is not one of these. */
export function stepKey<K>(keys: K[], current: K, key: string): K | null {
  const i = keys.indexOf(current);
  const to = key === 'ArrowRight' || key === 'ArrowDown' ? i + 1 : key === 'ArrowLeft' || key === 'ArrowUp' ? i - 1 : key === 'Home' ? 0 : key === 'End' ? keys.length - 1 : null;
  return to === null ? null : keys[(to + keys.length) % keys.length]!;
}

/** `iconOnly` — the label is read by the screen reader, the icon is shown. */
export interface SegmentItem<V extends string> { value: V; label: string; icon?: string; iconOnly?: boolean; disabled?: boolean }

/**
 * A few options side by side, one chosen (rights «нет / чтение / правка», the theme, the step of a grid): the plate under the
 * chosen one slides to the next. A radio group for the screen reader: arrows move the choice.
 */
export function Segmented<V extends string>({ value, onChange, items, label, size = 'md', className }: {
  value: V | null; onChange: (v: V) => void; items: SegmentItem<V>[]; label: string; size?: 'sm' | 'md'; className?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const mark = useMarker(box, '[aria-checked="true"]', [value, items.length]);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const keys = items.filter((t) => !t.disabled).map((t) => t.value);
    const next = stepKey(keys, value ?? keys[0]!, e.key);
    if (next === null) return;
    e.preventDefault();
    onChange(next);
    requestAnimationFrame(() => box.current?.querySelector<HTMLElement>(`[data-seg="${next}"]`)?.focus());
  };
  // One stop of Tab: the chosen item, or the first one that can be chosen — never a switched-off button.
  const focusable = items.find((t) => t.value === value && !t.disabled)?.value ?? items.find((t) => !t.disabled)?.value;
  return (
    <div ref={box} role="radiogroup" aria-label={label} className={cx(s.seg, size === 'sm' && s.segSm, className)} onKeyDown={onKey}>
      {mark.shown && <span className={cx(s.segMark, mark.moving && s.markMoving)} style={mark.style} aria-hidden="true" />}
      {items.map((t) => (
        <button key={t.value} type="button" role="radio" data-seg={t.value} aria-checked={t.value === value} tabIndex={t.value === focusable ? 0 : -1}
          disabled={t.disabled} aria-label={t.iconOnly ? t.label : undefined} className={cx(s.segItem, t.iconOnly && s.segIcon)} onClick={() => onChange(t.value)}>
          {t.icon && <Icon name={t.icon} size={14} />}{!t.iconOnly && t.label}
        </button>
      ))}
    </div>
  );
}

/** A tab-looking trigger in the row of `Tabs` (the «Ещё» of a card): the same height, colour and arrow. */
export const TabTrigger = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(function TabTrigger({ children, className, ...rest }, ref) {
  return <button ref={ref} type="button" {...rest} className={cx(s.tab, className)}>{children}<Chevron size={12} className={s.caret} /></button>;
});

/**
 * The one search field (the top line, the head of a list column, a page): the magnifier, the text, the key that focuses it, and a cross
 * that clears it. Esc clears a filled field, then leaves it. `slash` — «/» focuses this field (one per page: its main search);
 * `onClose` — the field is opened over the line (a phone): the cross and Esc close it.
 */
export const SearchField = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'size'> & {
  value: string; onChange: (v: string) => void; label: string; size?: 'sm' | 'md' | 'lg';
  /** Quiet in a line of tools: no frame until focused. */
  ghost?: boolean;
  slash?: boolean; onClose?: () => void;
}>(function SearchField({ value, onChange, label, size = 'md', ghost, slash, onClose, className, onKeyDown, ...rest }, ref) {
  const inner = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!slash) return;
    const on = (e: globalThis.KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key === '/' && !t.closest('input, textarea, select, [contenteditable]')) { e.preventDefault(); inner.current?.focus(); }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [slash]);
  const clear = () => { onChange(''); if (onClose) onClose(); else inner.current?.focus(); };
  return (
    <span className={cx(s.search, size !== 'md' && s[size], ghost && s.searchGhost, className)}>
      <Icon name="search" size={size === 'lg' ? 16 : 14} className={s.searchIcon} />
      <input {...rest} ref={(el) => { inner.current = el; setRef(ref, el); }} type="search" aria-label={label} value={value}
        className={s.searchInput} onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.key !== 'Escape' || e.defaultPrevented) return;
          e.stopPropagation();
          if (value) { e.preventDefault(); onChange(''); if (!onClose) return; }
          e.currentTarget.blur(); onClose?.();
        }} />
      {value || onClose
        ? <button type="button" className={s.searchClear} aria-label="Очистить поиск" onClick={clear}><Icon name="close" size={12} /></button>
        : slash && <kbd className={s.searchKey} aria-hidden="true">/</kbd>}
    </span>
  );
});

/**
 * A number that changes softly: the new value rolls in from below when it grows and from above when it falls.
 * The old value is not kept in the DOM; a screen reader reads only the current one.
 */
export function Count({ value, className }: { value: number | string; className?: string }) {
  // The previous value lives in state and is replaced during render (React's «storing information from previous renders»):
  // no ref is written while rendering, so a repeated render gives the same direction.
  const [seen, setSeen] = useState<{ value: number | string; dir: 'up' | 'down' | null }>({ value, dir: null });
  if (seen.value !== value) {
    const n = (v: number | string) => Number(String(v).replace(/[^\d.-]/g, ''));
    setSeen({ value, dir: n(value) < n(seen.value) ? 'down' : 'up' });
  }
  const dir = seen.value === value ? seen.dir : null;
  return <span className={cx(s.count, className)}><span key={String(value)} className={s.countValue} data-dir={dir ?? undefined}>{value}</span></span>;
}
